// Single source of truth for per-page SEO metadata.
// Replaces the legacy MetaManager + SimpleMetaTags (both deleted).
//
// Uses react-helmet-async (HelmetProvider mounted in src/main.tsx).
//
// Guarantees:
//  - Same noindex allow-list as legacy MetaManager (login, signup,
//    persona-selection, password reset, verify-email, payment-return, etc.)
//  - Self-canonical https://behtari.pk{pathname} unless overridden
//  - JSON-LD blobs are scoped via data-behtari-schema so they never
//    collide with the static Organization schema in index.html
//  - Zero DOM mutation outside Helmet — no document.title side-effects

import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import {
  SITE_ORIGIN,
  SITE_NAME,
  DEFAULT_OG_IMAGE,
  isNoIndexPath,
} from "@/lib/seo/routeMeta";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  canonical?: string;
  noIndex?: boolean;
  noFollow?: boolean;
  type?: "website" | "article";
  /** Optional JSON-LD blobs. Each blob gets a stable scope key so
   *  duplicate writes are cleanly replaced. */
  jsonLd?: Array<{ key: string; data: object }>;
}

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  image,
  canonical,
  noIndex,
  noFollow,
  type = "website",
  jsonLd = [],
}) => {
  const location = useLocation();
  const pathname = location.pathname || "/";
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const canonicalUrl = canonical || `${SITE_ORIGIN}${pathname}`;
  const ogImage = image
    ? image.startsWith("http")
      ? image
      : `${SITE_ORIGIN}${image}`
    : `${SITE_ORIGIN}${DEFAULT_OG_IMAGE}`;

  const shouldNoIndex = noIndex || isNoIndexPath(pathname);
  const robotsParts: string[] = [];
  robotsParts.push(shouldNoIndex ? "noindex" : "index");
  robotsParts.push(noFollow ? "nofollow" : "follow");
  const robots = robotsParts.join(", ");

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_PK" />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@behtari_pk" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD blobs — scoped via data-behtari-schema to avoid
          colliding with the static Organization schema in index.html. */}
      {jsonLd.map((blob) => (
        <script
          key={blob.key}
          type="application/ld+json"
          data-behtari-schema={blob.key}
        >
          {JSON.stringify(blob.data)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
